import React, { useState, useEffect, useCallback } from 'react';
import styled, { createGlobalStyle } from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { useCustomerAuth } from '../contexts/CustomerAuthContext';
import { 
  ArrowLeft, 
  Star, 
  Gift, 
  History, 
  FileText, 
  LogOut,
  Trophy,
  TrendingUp
} from 'lucide-react';
import { useToast } from '../contexts/ToastContext';
import { QRCodeCanvas } from 'qrcode.react';

interface Customer {
  id: number;
  name: string;
  cpf: string;
  total_points: number;
  store: {
    id: number;
    name: string;
    description?: string;
  };
}

interface Reward {
  id: number;
  name: string;
  description: string;
  points_required: number;
  stock_quantity: number;
  image_url?: string;
}

interface Transaction {
  id: number;
  type: 'earned' | 'redeemed';
  points: number;
  description: string;
  created_at: string;
}

interface CompanyRule {
  id: number;
  title: string;
  description: string;
  order: number;
  is_active: boolean;
}

interface DashboardData {
  customer: Customer;
  current_points: number;
  current_tier?: {
    id: number;
    name: string;
    rank: number;
    points_multiplier: string;
    benefits?: Array<{
      id: number;
      name: string;
      description?: string | null;
      is_active: boolean;
    }>;
  } | null;
  available_rewards: Reward[];
  recent_transactions: Transaction[];
  statistics: {
    total_points_earned: number;
    total_points_redeemed: number;
    total_redemptions: number;
  };
}

interface Voucher {
  id: number;
  code: string;
  status: 'issued' | 'used' | 'cancelled' | 'expired';
  issued_at: string;
  used_at?: string | null;
  expires_at?: string | null;
  reward?: Reward;
}

const Container = styled.div`
  min-height: 100vh;
  background: #f8f9fa;
  padding: 20px;
`;

const Header = styled.div`
  background: linear-gradient(135deg, #00d4aa 0%, #00b894 100%);
  color: white;
  padding: 32px;
  border-radius: 16px;
  margin-bottom: 32px;
  position: relative;
`;

const BackButton = styled.button`
  position: absolute;
  top: 24px;
  left: 24px;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  background: rgba(255, 255, 255, 0.2);
  border: 1px solid rgba(255, 255, 255, 0.3);
  border-radius: 6px;
  color: white;
  font-size: 14px;
  transition: all 0.2s ease;

  &:hover {
    background: rgba(255, 255, 255, 0.3);
  }
`;

const LogoutButton = styled.button`
  position: absolute;
  top: 24px;
  right: 24px;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  background: rgba(255, 255, 255, 0.2);
  border: 1px solid rgba(255, 255, 255, 0.3);
  border-radius: 6px;
  color: white;
  font-size: 14px;
  transition: all 0.2s ease;

  &:hover {
    background: rgba(255, 255, 255, 0.3);
  }
`;

const WelcomeText = styled.div`
  text-align: center;
  margin-top: 40px;
`;

const CustomerName = styled.h1`
  font-size: 32px;
  font-weight: 600;
  margin: 0 0 8px;
`;

const StoreName = styled.p`
  font-size: 18px;
  opacity: 0.9;
  margin: 0;
`;

const PointsDisplay = styled.div`
  background: rgba(255, 255, 255, 0.15);
  padding: 24px;
  border-radius: 12px;
  text-align: center;
  margin: 32px auto 0;
  max-width: 300px;
`;

const TierBadge = styled.div`
  margin: 14px auto 0;
  width: fit-content;
  background: rgba(15, 23, 42, 0.22);
  border: 1px solid rgba(255, 255, 255, 0.35);
  padding: 8px 14px;
  border-radius: 999px;
  font-size: 13px;
  font-weight: 700;
`;

const TierBenefits = styled.div`
  margin-top: 8px;
  font-size: 12px;
  opacity: 0.95;
`;

const PointsLabel = styled.div`
  font-size: 16px;
  opacity: 0.9;
  margin-bottom: 8px;
`;

const PointsValue = styled.div`
  font-size: 48px;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
`;

const TabsContainer = styled.div`
  display: flex;
  gap: 16px;
  margin-bottom: 32px;
  overflow-x: auto;
`;

const Tab = styled.button<{ active: boolean }>`
  padding: 12px 24px;
  background: ${props => props.active ? 'white' : 'transparent'};
  color: ${props => props.active ? '#333' : '#666'};
  border: 2px solid ${props => props.active ? '#00d4aa' : '#e9ecef'};
  border-radius: 8px;
  font-weight: 500;
  white-space: nowrap;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: 8px;

  &:hover {
    border-color: #00d4aa;
    color: #333;
  }
`;

const ContentArea = styled.div`
  background: white;
  border-radius: 12px;
  padding: 32px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 24px;
  margin-bottom: 32px;
`;

const StatCard = styled.div`
  background: #f8f9fa;
  padding: 24px;
  border-radius: 12px;
  text-align: center;
`;

const StatIcon = styled.div`
  width: 48px;
  height: 48px;
  border-radius: 12px;
  background: #00d4aa20;
  color: #00d4aa;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 16px;
`;

const StatValue = styled.div`
  font-size: 24px;
  font-weight: 600;
  color: #333;
  margin-bottom: 8px;
`;

const StatLabel = styled.div`
  font-size: 14px;
  color: #666;
`;

const RewardsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 24px;
`;

const RewardCard = styled.div`
  border: 2px solid #e9ecef;
  border-radius: 12px;
  padding: 24px;
  transition: all 0.2s ease;

  &:hover {
    border-color: #00d4aa;
    box-shadow: 0 4px 12px rgba(102, 126, 234, 0.1);
  }
`;

const RewardName = styled.h3`
  font-size: 18px;
  font-weight: 600;
  color: #333;
  margin: 0 0 8px;
`;

const RewardDescription = styled.p`
  font-size: 14px;
  color: #666;
  margin: 0 0 16px;
`;

const RewardFooter = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const RewardPoints = styled.div`
  font-size: 16px;
  font-weight: 600;
  color: #00d4aa;
`;

const RedeemButton = styled.button<{ disabled: boolean }>`
  padding: 8px 16px;
  background: ${props => props.disabled ? '#e9ecef' : '#28a745'};
  color: ${props => props.disabled ? '#666' : 'white'};
  border: none;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 500;
  cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
  transition: all 0.2s ease;

  &:hover {
    background: ${props => props.disabled ? '#e9ecef' : '#218838'};
  }
`;

const TransactionList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const TransactionItem = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px;
  background: #f8f9fa;
  border-radius: 8px;
`;

const TransactionInfo = styled.div`
  flex: 1;
`;

const TransactionDescription = styled.div`
  font-weight: 500;
  color: #333;
  margin-bottom: 4px;
`;

const TransactionDate = styled.div`
  font-size: 12px;
  color: #666;
`;

const TransactionPoints = styled.div<{ type: string }>`
  font-size: 16px;
  font-weight: 600;
  color: ${props => props.type === 'earned' ? '#28a745' : '#dc3545'};
`;

const VoucherGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
  gap: 16px;
`;

const VoucherCard = styled.div`
  background: white;
  border-radius: 12px;
  border: 1px solid #e9ecef;
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const VoucherHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
`;

const VoucherCode = styled.div`
  font-weight: 700;
  color: #333;
  letter-spacing: 1px;
`;

const VoucherStatus = styled.span<{ status: Voucher['status'] }>`
  padding: 4px 10px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  background: ${props => {
    switch (props.status) {
      case 'issued': return '#e0f2fe';
      case 'used': return '#dcfce7';
      case 'expired': return '#fee2e2';
      default: return '#f3f4f6';
    }
  }};
  color: ${props => {
    switch (props.status) {
      case 'issued': return '#0369a1';
      case 'used': return '#166534';
      case 'expired': return '#991b1b';
      default: return '#374151';
    }
  }};
`;

const VoucherButton = styled.button`
  padding: 10px 14px;
  border-radius: 8px;
  background: #00d4aa;
  color: white;
  font-weight: 600;
  transition: background 0.2s ease;

  &:hover {
    background: #00b894;
  }
`;

const VoucherModalOverlay = styled.div<{ $open: boolean }>`
  display: ${props => props.$open ? 'flex' : 'none'};
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.55);
  align-items: center;
  justify-content: center;
  z-index: 2000;
  padding: 16px;
`;

const VoucherModalContent = styled.div`
  background: white;
  border-radius: 16px;
  padding: 24px;
  max-width: 420px;
  width: 100%;
  text-align: center;
`;

const VoucherModalTitle = styled.h3`
  font-size: 20px;
  font-weight: 700;
  color: #333;
  margin-bottom: 8px;
`;

const VoucherModalMeta = styled.div`
  color: #6b7280;
  font-size: 14px;
  margin-bottom: 16px;
`;

const VoucherQRCode = styled.div`
  display: flex;
  justify-content: center;
  padding: 16px;
  border-radius: 12px;
  background: #f8fafc;
  margin-bottom: 16px;
`;

const VoucherPrintArea = styled.div`
  &.voucher-print {
    display: flex;
    flex-direction: column;
    gap: 12px;
  }
`;

const VoucherModalActions = styled.div`
  display: flex;
  gap: 12px;
  margin-top: 16px;
  justify-content: center;
  flex-wrap: wrap;
`;

const VoucherSecondaryButton = styled.button`
  padding: 10px 14px;
  border-radius: 8px;
  border: 1px solid #e5e7eb;
  background: white;
  color: #374151;
  font-weight: 600;
`;

const PrintStyles = createGlobalStyle`
  @media print {
    body * {
      visibility: hidden;
    }
    .voucher-print, .voucher-print * {
      visibility: visible;
    }
    .voucher-print {
      position: fixed;
      inset: 0;
      padding: 24px;
      background: white;
    }
  }
`;

const RulesList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const RuleItem = styled.div`
  background: #f8f9fa;
  padding: 24px;
  border-radius: 12px;
  border-left: 4px solid #00d4aa;
`;

const RuleTitle = styled.h4`
  font-size: 18px;
  font-weight: 600;
  color: #333;
  margin: 0 0 12px;
`;

const RuleDescription = styled.p`
  font-size: 14px;
  color: #666;
  line-height: 1.6;
  margin: 0;
  white-space: pre-wrap;
`;

const EmptyRulesMessage = styled.div`
  text-align: center;
  padding: 48px 24px;
  color: #666;
  font-size: 14px;
`;

export const CustomerDashboardPage: React.FC = () => {
  const navigate = useNavigate();
  const { token, logout } = useCustomerAuth();
  const { showSuccess, showError } = useToast();
  const [activeTab, setActiveTab] = useState('overview');
  const [dashboardData, setDashboardData] = useState<DashboardData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [companyRules, setCompanyRules] = useState<CompanyRule[]>([]);
  const [vouchers, setVouchers] = useState<Voucher[]>([]);
  const [selectedVoucher, setSelectedVoucher] = useState<Voucher | null>(null);
  const [showVoucherModal, setShowVoucherModal] = useState(false);

  const loadDashboardData = useCallback(async () => {
    try {
      if (!token) {
        navigate('/customer/login');
        return;
      }

      const apiUrl = process.env.REACT_APP_API_URL || '/api';
      const response = await fetch(`${apiUrl}/customer/dashboard`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Accept': 'application/json',
        },
      });

      const data = await response.json();

      if (data.success) {
        setDashboardData(data.data);
      } else {
        setError(data.message || 'Erro ao carregar dados');
      }
    } catch (error) {
      setError('Erro de conexão');
    } finally {
      setLoading(false);
    }
  }, [navigate, token]);

  const loadCompanyRules = useCallback(async () => {
    try {
      if (!token) {
        return;
      }

      const apiUrl = process.env.REACT_APP_API_URL || '/api';
      const response = await fetch(`${apiUrl}/customer/store-rules`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Accept': 'application/json',
        },
      });

      const data = await response.json();

      if (data.success) {
        setCompanyRules(data.data);
      }
    } catch (error) {
      console.error('Erro ao carregar regras:', error);
    }
  }, [token]);

  const loadVouchers = useCallback(async () => {
    try {
      if (!token) {
        return;
      }

      const apiUrl = process.env.REACT_APP_API_URL || '/api';
      const response = await fetch(`${apiUrl}/customer/vouchers`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Accept': 'application/json',
        },
      });

      const data = await response.json();

      if (data.success) {
        setVouchers(data.data || []);
      }
    } catch (error) {
      console.error('Erro ao carregar vouchers:', error);
    }
  }, [token]);

  useEffect(() => {
    if (!token) {
      navigate('/customer/login');
      return;
    }
    loadDashboardData();
    loadCompanyRules();
    loadVouchers();
  }, [token, navigate, loadCompanyRules, loadDashboardData, loadVouchers]);

  const handleRedeemReward = async (rewardId: number) => {
    try {
      const apiUrl = process.env.REACT_APP_API_URL || '/api';
      const response = await fetch(`${apiUrl}/customer/redeem/${rewardId}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Accept': 'application/json',
        },
      });

      const data = await response.json();

      if (data.success) {
        showSuccess('Prêmio resgatado com sucesso!');
        if (data.data?.voucher) {
          setSelectedVoucher(data.data.voucher);
          setShowVoucherModal(true);
        }
        loadDashboardData();
        loadVouchers();
      } else {
        showError(data.message || 'Erro ao resgatar prêmio');
      }
    } catch (error) {
      showError('Erro de conexão');
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/customer/login');
  };

  const handleBack = () => {
    navigate('/');
  };

  const handleOpenVoucher = (voucher: Voucher) => {
    setSelectedVoucher(voucher);
    setShowVoucherModal(true);
  };

  const handlePrintVoucher = () => {
    window.print();
  };

  if (loading) {
    return (
      <Container>
        <div style={{ textAlign: 'center', padding: '48px' }}>
          Carregando seus dados...
        </div>
      </Container>
    );
  }

  if (error || !dashboardData) {
    return (
      <Container>
        <div style={{ textAlign: 'center', padding: '48px' }}>
          <p>{error || 'Erro ao carregar dados'}</p>
          <button onClick={() => navigate('/customer/login')}>
            Voltar ao Login
          </button>
        </div>
      </Container>
    );
  }

  const renderContent = () => {
    switch (activeTab) {
      case 'overview':
        return (
          <>
            <StatsGrid>
              <StatCard>
                <StatIcon>
                  <TrendingUp size={24} />
                </StatIcon>
                <StatValue>{dashboardData.statistics.total_points_earned}</StatValue>
                <StatLabel>Total de Pontos Ganhos</StatLabel>
              </StatCard>
              <StatCard>
                <StatIcon>
                  <Gift size={24} />
                </StatIcon>
                <StatValue>{dashboardData.statistics.total_redemptions}</StatValue>
                <StatLabel>Resgates Realizados</StatLabel>
              </StatCard>
              <StatCard>
                <StatIcon>
                  <Trophy size={24} />
                </StatIcon>
                <StatValue>{dashboardData.statistics.total_points_redeemed}</StatValue>
                <StatLabel>Pontos Utilizados</StatLabel>
              </StatCard>
            </StatsGrid>

            <h3>Últimas Transações</h3>
            <TransactionList>
              {dashboardData.recent_transactions.slice(0, 5).map((transaction) => (
                <TransactionItem key={transaction.id}>
                  <TransactionInfo>
                    <TransactionDescription>{transaction.description}</TransactionDescription>
                    <TransactionDate>
                      {new Date(transaction.created_at).toLocaleDateString('pt-BR')}
                    </TransactionDate>
                  </TransactionInfo>
                  <TransactionPoints type={transaction.type}>
                    {transaction.type === 'earned' ? '+' : '-'}{transaction.points} pts
                  </TransactionPoints>
                </TransactionItem>
              ))}
            </TransactionList>
          </>
        );

      case 'rewards':
        return (
          <>
            <h3>Prêmios Disponíveis</h3>
            <RewardsGrid>
              {dashboardData.available_rewards.map((reward) => (
                <RewardCard key={reward.id}>
                  {reward.image_url && (
                    <div style={{ marginBottom: '12px', textAlign: 'center' }}>
                      <img
                        src={reward.image_url}
                        alt={reward.name}
                        style={{ maxWidth: '100%', maxHeight: '150px', borderRadius: '8px', objectFit: 'cover' }}
                      />
                    </div>
                  )}
                  <RewardName>{reward.name}</RewardName>
                  <RewardDescription>{reward.description}</RewardDescription>
                  <RewardFooter>
                    <RewardPoints>{reward.points_required} pontos</RewardPoints>
                    <RedeemButton
                      disabled={dashboardData.current_points < reward.points_required}
                      onClick={() => handleRedeemReward(reward.id)}
                    >
                      {dashboardData.current_points < reward.points_required ? 'Insuficiente' : 'Resgatar'}
                    </RedeemButton>
                  </RewardFooter>
                </RewardCard>
              ))}
            </RewardsGrid>
          </>
        );

      case 'history':
        return (
          <>
            <h3>Histórico Completo</h3>
            <TransactionList>
              {dashboardData.recent_transactions.map((transaction) => (
                <TransactionItem key={transaction.id}>
                  <TransactionInfo>
                    <TransactionDescription>{transaction.description}</TransactionDescription>
                    <TransactionDate>
                      {new Date(transaction.created_at).toLocaleString('pt-BR')}
                    </TransactionDate>
                  </TransactionInfo>
                  <TransactionPoints type={transaction.type}>
                    {transaction.type === 'earned' ? '+' : '-'}{transaction.points} pts
                  </TransactionPoints>
                </TransactionItem>
              ))}
            </TransactionList>
          </>
        );

      case 'vouchers':
        return (
          <>
            <h3>Meus Vouchers</h3>
            {vouchers.length === 0 ? (
              <EmptyRulesMessage>
                <p>Nenhum voucher disponível.</p>
              </EmptyRulesMessage>
            ) : (
              <VoucherGrid>
                {vouchers.map((voucher) => (
                  <VoucherCard key={voucher.id}>
                    <VoucherHeader>
                      <VoucherCode>{voucher.code}</VoucherCode>
                      <VoucherStatus status={voucher.status}>
                        {voucher.status === 'issued' && 'Disponível'}
                        {voucher.status === 'used' && 'Usado'}
                        {voucher.status === 'expired' && 'Expirado'}
                        {voucher.status === 'cancelled' && 'Cancelado'}
                      </VoucherStatus>
                    </VoucherHeader>
                    <div>
                      <strong>{voucher.reward?.name || 'Prêmio'}</strong>
                    </div>
                    <div>Emitido em: {new Date(voucher.issued_at).toLocaleString('pt-BR')}</div>
                    {voucher.expires_at && (
                      <div>Expira em: {new Date(voucher.expires_at).toLocaleDateString('pt-BR')}</div>
                    )}
                    <VoucherButton onClick={() => handleOpenVoucher(voucher)}>
                      Ver Voucher
                    </VoucherButton>
                  </VoucherCard>
                ))}
              </VoucherGrid>
            )}
          </>
        );

      case 'rules':
        return (
          <>
            <h3>Regras do Programa de Fidelidade</h3>
            {companyRules.length > 0 ? (
              <RulesList>
                {companyRules.map((rule) => (
                  <RuleItem key={rule.id}>
                    <RuleTitle>{rule.title}</RuleTitle>
                    <RuleDescription>{rule.description}</RuleDescription>
                  </RuleItem>
                ))}
              </RulesList>
            ) : (
              <EmptyRulesMessage>
                <p>Nenhuma regra cadastrada ainda.</p>
                <p>As regras do programa de fidelidade aparecerão aqui assim que forem configuradas pela loja.</p>
              </EmptyRulesMessage>
            )}
          </>
        );

      default:
        return null;
    }
  };

  return (
    <Container>
      <Header>
        <BackButton onClick={handleBack}>
          <ArrowLeft size={16} />
          Voltar
        </BackButton>
        <LogoutButton onClick={handleLogout}>
          <LogOut size={16} />
          Sair
        </LogoutButton>
        
        <WelcomeText>
          <CustomerName>{dashboardData.customer.name}</CustomerName>
          <StoreName>{dashboardData.customer.store.name}</StoreName>
        </WelcomeText>

        <PointsDisplay>
          <PointsLabel>Seus Pontos</PointsLabel>
          <PointsValue>
            <Star size={32} />
            {dashboardData.current_points}
          </PointsValue>
          {dashboardData.current_tier && (
            <>
              <TierBadge>
                Nivel {dashboardData.current_tier.rank}: {dashboardData.current_tier.name}
              </TierBadge>
              <TierBenefits>
                Multiplicador: x{dashboardData.current_tier.points_multiplier}
              </TierBenefits>
            </>
          )}
        </PointsDisplay>
      </Header>

      <TabsContainer>
        <Tab active={activeTab === 'overview'} onClick={() => setActiveTab('overview')}>
          <TrendingUp size={16} />
          Resumo
        </Tab>
        <Tab active={activeTab === 'rewards'} onClick={() => setActiveTab('rewards')}>
          <Gift size={16} />
          Prêmios
        </Tab>
        <Tab active={activeTab === 'history'} onClick={() => setActiveTab('history')}>
          <History size={16} />
          Histórico
        </Tab>
        <Tab active={activeTab === 'vouchers'} onClick={() => setActiveTab('vouchers')}>
          <FileText size={16} />
          Vouchers
        </Tab>
        <Tab active={activeTab === 'rules'} onClick={() => setActiveTab('rules')}>
          <FileText size={16} />
          Regras
        </Tab>
      </TabsContainer>

      <ContentArea>
        {renderContent()}
      </ContentArea>

      <VoucherModalOverlay $open={showVoucherModal && !!selectedVoucher}>
        {selectedVoucher && (
          <VoucherModalContent>
            <PrintStyles />
            <VoucherPrintArea className="voucher-print">
              <VoucherModalTitle>Voucher de Resgate</VoucherModalTitle>
              <VoucherModalMeta>
                {selectedVoucher.reward?.name || 'Prêmio'}
              </VoucherModalMeta>
              <VoucherQRCode>
                <QRCodeCanvas value={`PONTOFIEL:VOUCHER:${selectedVoucher.code}`} size={180} />
              </VoucherQRCode>
              <div><strong>Código:</strong> {selectedVoucher.code}</div>
              {selectedVoucher.expires_at && (
                <div>Validade: {new Date(selectedVoucher.expires_at).toLocaleDateString('pt-BR')}</div>
              )}
            </VoucherPrintArea>
            <VoucherModalActions>
              <VoucherButton onClick={handlePrintVoucher}>Imprimir Voucher</VoucherButton>
              <VoucherSecondaryButton onClick={() => setShowVoucherModal(false)}>
                Fechar
              </VoucherSecondaryButton>
            </VoucherModalActions>
          </VoucherModalContent>
        )}
      </VoucherModalOverlay>
    </Container>
  );
};
